import React from 'react';
import { useParallax } from 'react-scroll-parallax';
import './AboutPages.css';
// Import the background image
import generativeBgImage from '../assets/images/generative-bg.jpg';
// Import the ValuesWithCanvasEffect component
import ValuesWithCanvasEffect from '../components/ValuesWithCanvasEffect';
// Import the AnimatedCounter component
import AnimatedCounter from '../components/AnimatedCounter';

const AboutCompany = () => {
  const titleParallax = useParallax({
    translateY: [20, -20],
    opacity: [0.8, 1],
    scale: [0.95, 1],
    easing: 'easeInQuad',
    startScroll: 0,
    endScroll: 300
  });

  const contentParallax = useParallax({
    translateY: [50, 0],
    opacity: [0.6, 1],
    scale: [0.98, 1],
    easing: 'easeOutQuad',
    startScroll: 0,
    endScroll: 500
  });

  const statsParallax = useParallax({
    translateY: [80, 0],
    opacity: [0.4, 1],
    scale: [0.95, 1],
    easing: 'easeOutQuad',
    startScroll: 200,
    endScroll: 600
  });

  const valuesParallax = useParallax({
    translateY: [60, 0],
    opacity: [0.5, 1],
    scale: [0.96, 1],
    easing: 'easeOutQuad',
    startScroll: 600,
    endScroll: 1000
  });

  // Values data with icons and colors for canvas effect
  const valuesData = [
    {
      title: "Trust & Transparency",
      description: "We build AI that is explainable, transparent, and worthy of our clients' trust.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
        </svg>
      ),
      colors: [[59, 130, 246], [37, 99, 235]],
      bgColor: "bg-blue-900"
    },
    {
      title: "Global Impact",
      description: "We develop AI solutions that create positive change across industries and borders.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      ),
      colors: [[139, 92, 246], [124, 58, 237]],
      bgColor: "bg-purple-900"
    },
    {
      title: "Innovation",
      description: "We continuously push the boundaries of what's possible with artificial intelligence.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"></path>
          <line x1="16" y1="8" x2="2" y2="22"></line>
          <line x1="17.5" y1="15" x2="9" y2="15"></line>
        </svg>
      ),
      colors: [[236, 72, 153], [219, 39, 119]],
      bgColor: "bg-pink-900"
    },
    {
      title: "Client Partnership",
      description: "We work closely with our clients to understand their unique challenges and goals.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      ),
      colors: [[245, 158, 11], [217, 119, 6]],
      bgColor: "bg-amber-900"
    },
    {
      title: "Integrity",
      description: "Ensuring fairness, transparency, and ethical responsibility in AI-driven insights, enabling businesses to make trusted, data-backed decisions.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
        </svg>
      ),
      colors: [[16, 185, 129], [5, 150, 105]],
      bgColor: "bg-emerald-900"
    },
    {
      title: "Sustainability",
      description: "Leveraging AI to drive smarter, more resource-efficient business operations while delivering long-term, sustainable insights for growth and resilience.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-10 w-10">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
      ),
      colors: [[239, 68, 68], [220, 38, 38]],
      bgColor: "bg-red-900"
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg" style={{ backgroundImage: `url(${generativeBgImage})` }}>
          <div className="about-hero-overlay"></div>
        </div>
        <div ref={titleParallax.ref} className="about-hero-content">
          <h1 className="about-hero-title">Empowering Business with Intelligent AI Solutions</h1>
          <p className="about-hero-subtitle">Transforming industries through innovative artificial intelligence that drives real results</p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="about-section">
        <div className="about-container">
          <div ref={contentParallax.ref} className="about-content">
            <h2 className="about-section-title">Our Mission</h2>
            <div className="about-text-content">
              <p>
                At Hyperion AI, we believe that artificial intelligence should be accessible, practical, and transformative. Founded in 2020 by a team of passionate AI researchers and business innovators, we set out to bridge the gap between cutting-edge technology and real-world business challenges.
              </p>
              <p>
                Our solutions don't just analyze data—they solve problems. Whether it's predicting equipment failures before they happen, automating quality control with computer vision, or creating intelligent chatbots that understand your customers, we deliver AI that works for your business, not the other way around.
              </p>
              <p>
                Today, we've helped over 200 enterprises across healthcare, finance, manufacturing, and beyond unlock the power of AI. Our commitment goes beyond technology—we build lasting partnerships that drive sustainable growth and competitive advantage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="stats-section">
        <div className="about-container">
          <div ref={statsParallax.ref} className="stats-grid">
            <div className="stat-card">
              <h3 className="stat-number"><AnimatedCounter end={200} suffix="+" /></h3>
              <p className="stat-label">Enterprise Clients</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number"><AnimatedCounter end={50} suffix="+" /></h3>
              <p className="stat-label">AI Experts</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number"><AnimatedCounter end={15} suffix="+" /></h3>
              <p className="stat-label">Countries Served</p>
            </div>
            <div className="stat-card">
              <h3 className="stat-number"><AnimatedCounter end={98} suffix="%" /></h3>
              <p className="stat-label">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section with Canvas Reveal Effect */}
      <section className="values-section">
        <div className="about-container">
          <h2 className="about-section-title">Our Values</h2>
          <div ref={valuesParallax.ref}>
            <ValuesWithCanvasEffect valuesData={valuesData} />
          </div>
        </div>
      </section>

      {/* CTA Section - Adding this to align with home page */}
      <section className="cta-section">
        <div className="section-background">
          <img src={process.env.PUBLIC_URL + "/images/homepageBg.jpg"} alt="CTA Background" className="cta-bg-image" />
          <div className="cta-gradient-bg"></div>
        </div>
        <div className="container">
          <h2 className="cta-title">Ready to Transform Your Business with AI?</h2>
          <p className="cta-description">Join leading companies who trust Hyperion AI to deliver intelligent solutions that drive efficiency, innovation, and growth. Let's build the future of your business together.</p>
          <a href="/contact-us" className="cta-button">Start Your AI Journey</a>
        </div>
      </section>
    </div>
  );
};

export default AboutCompany;
